import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWishlist } from '@/hooks/useWishlist';
import { useCart } from '@/hooks/useCart';
import type { Product } from '@/types';

interface ProductCardProps {
  product: Product;
  showBadge?: boolean;
}

export default function ProductCard({ product, showBadge = true }: ProductCardProps) {
  const { toggleWishlist, isWishlisted } = useWishlist();
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAddToCart = () => {
    addToCart(product, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const wishlisted = isWishlisted(product.id);

  return (
    <div className="group relative bg-white rounded-2xl border border-cream-300 overflow-hidden hover:shadow-lg transition-all duration-300">
      {/* Image */}
      <Link to={`/product/${product.slug}`} className="block relative aspect-[3/4] overflow-hidden bg-cream-100">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {showBadge && product.badge && (
          <span className="absolute top-3 right-3 bg-charcoal-900 text-white text-[10px] font-bold px-3 py-1.5 rounded-full">
            {product.badge}
          </span>
        )}
        {product.oldPrice && (
          <span className="absolute top-3 left-3 bg-blush-200 text-charcoal-900 text-[10px] font-bold px-3 py-1.5 rounded-full">
            خصم
          </span>
        )}
        {/* Quick Actions */}
        <div className="absolute bottom-3 left-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
          <button
            onClick={handleAddToCart}
            disabled={!product.inStock}
            className={`flex-1 py-1.5 md:py-2.5 rounded-xl text-[10px] md:text-xs font-bold transition-colors whitespace-nowrap ${
              !product.inStock
                ? 'bg-cream-200 text-charcoal-400 cursor-not-allowed'
                : added
                ? 'bg-green-600 text-white'
                : 'bg-charcoal-900 text-white hover:bg-charcoal-800'
            }`}
          >
            {added ? 'تمت الإضافة' : 'أضيفي إلى السلة'}
          </button>
          <button
            onClick={() => toggleWishlist(product.id)}
            className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors ${
              wishlisted
                ? 'bg-blush-200 text-charcoal-900'
                : 'bg-white text-charcoal-600 hover:bg-cream-200'
            }`}
          >
            <i className={`${wishlisted ? 'ri-heart-fill' : 'ri-heart-line'}`} />
          </button>
        </div>
      </Link>

      {/* Info */}
      <div className="p-4">
        <p className="text-xs text-charcoal-400 mb-1">{product.category}</p>
        <Link to={`/product/${product.slug}`}>
          <h3 className="text-sm font-semibold text-charcoal-900 mb-2 line-clamp-1 hover:text-blush-300 transition-colors">
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center gap-2">
          <span className="text-base font-bold text-charcoal-900">₪{product.price}</span>
          {product.oldPrice && (
            <span className="text-sm text-charcoal-400 line-through">₪{product.oldPrice}</span>
          )}
        </div>
        {/* Rating - hidden */}
        {/*
        <div className="flex items-center gap-1 mt-2">
          <div className="flex">
            {Array.from({ length: 5 }).map((_, i) => (
              <i
                key={i}
                className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-xs ${
                  i < Math.floor(product.rating) ? 'text-amber-400' : 'text-charcoal-300'
                }`}
              />
            ))}
          </div>
          <span className="text-xs text-charcoal-400">({product.reviewCount})</span>
        </div>
        */}
      </div>
    </div>
  );
}